import { ReactNode, useMemo, useState } from 'react';
import { LoadingIndicator } from 'vopay';
import { BlockingLoaderService, LoaderContext, LoaderVisibilityContext } from './LoaderContext';

// Shared owns the fullscreen blocking loader (same flip as the ID-12901 feedback UI): shared renders
// vopay's LoadingIndicator itself and exposes useBlockingLoader() for shared code and hosts to call.
//
// Deliberately NOT part of SharedProviders, because it's position-sensitive: hosts mount LoaderProvider
// separately, above anything that fetches (e.g. login/session providers), while SharedProviders stays
// innermost. If more position-sensitive services ever show up we could revisit with a two-slot wrapper
// (SharedProviders + SharedRootProviders), but for one provider that's over-engineering.
//
// The rendered DOM is a Cypress contract: [alt="loader"] and [data-testid="loadingIndicator"][data-state]
// are asserted by ~40 spec files, so the LoadingIndicator element and its props must not change.
// renderIndicator=false keeps the service and visibility context working but
// renders no indicator — for embeds whose HOST owns the spinner (the iframe
// forwards visibility as LOADING messages; see the accounting IFrameBridge).
export default function LoaderProvider({
  children,
  renderIndicator = true,
}: {
  children: ReactNode;
  renderIndicator?: boolean;
}) {
  const [activeRequests, setActiveRequests] = useState(0);

  const isVisible = activeRequests > 0;

  // []-memoized on purpose: the service is actions-only, so show/hide flips never rebuild it and
  // action-only consumers (every fetch hook, ClientLoginProvider's ~110-field context) don't
  // re-render. Visibility goes out through its own context below.
  const service = useMemo<BlockingLoaderService>(
    () => ({
      show: () => setActiveRequests((n) => n + 1),
      // Callers sometimes hide more than they show — keep the floor.
      hide: () => setActiveRequests((n) => Math.max(n - 1, 0)),
      reset: () => setActiveRequests(0),
    }),
    [],
  );

  return (
    <LoaderContext.Provider value={service}>
      <LoaderVisibilityContext.Provider value={isVisible}>
        {children}
        {renderIndicator && <LoadingIndicator size="lg" isVisible={isVisible} noLeadingDots hasNoMessage hasNoTitle />}
      </LoaderVisibilityContext.Provider>
    </LoaderContext.Provider>
  );
}
